import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import i18n from 'i18n';
import {
  requestOrdersHistory,
  requestProfile,
  requestWalletApprove,
  requestWalletChangeFaceId,
  requestWallets,
  updateProfile,
  updateWallet,
} from 'store/api/cuverse-api/profile/profile.service';
import { IChangeWalletError } from 'store/api/cuverse-api/profile/types/change-wallet-error.interface';
import { IChangeWalletRequestBody } from 'store/api/cuverse-api/profile/types/change-wallet-request-body-interface';
import {
  ChangeWalletResponseSchema,
  TChangeWalletData,
} from 'store/api/cuverse-api/profile/types/change-wallet-response-interface';
import {
  OrdersHistoryResponseSchema,
  TOrdersHistoryData,
} from 'store/api/cuverse-api/profile/types/orders-history-response.interface';
import {
  ProfileDataResponseSchema,
  TProfileData,
} from 'store/api/cuverse-api/profile/types/profile-response.interface';
import { IUpdateProfileRequestBody } from 'store/api/cuverse-api/profile/types/update-profile-request-body.interface';
import { IUpdateWalletError } from 'store/api/cuverse-api/profile/types/update-wallet-error.interface';
import { IUpdateWalletRequestBody } from 'store/api/cuverse-api/profile/types/update-wallet-request-body.interface';
import { IWalletApproveRequestBody } from 'store/api/cuverse-api/profile/types/wallet-approve-request-body.interface';
import {
  TWalletsApproveData,
  WalletsApproveResponseSchema,
} from 'store/api/cuverse-api/profile/types/wallet-approve-response.interface';
import {
  TWalletsData,
  WalletsResponseSchema,
} from 'store/api/cuverse-api/profile/types/wallet-response.interface';
import { closePopupAction } from 'store/modals-reducer/modals.reducer';

import { requestWithZodSchema } from 'utils/common/requestWithZodSchema';
import { notifyError, notifySuccess } from 'utils/notify/notify.utils';

import { setNewWalletAddressAction, setWalletChangeStatusAction } from './profile.reducer';

export const requestProfileDataThunkAction = createAsyncThunk(
  'profile/fetchPersonalData',
  async (): Promise<TProfileData> =>
    await requestWithZodSchema(requestProfile, ProfileDataResponseSchema).catch((error) => {
      notifyError(i18n.t('notifyMessages.profile.dataLoadError'));
      throw error;
    }),
);

export const updateProfileDataThunkAction = createAsyncThunk(
  'profile/updatePersonalData',
  async ({
    body,
    onSuccessCb,
  }: {
    body: IUpdateProfileRequestBody;
    onSuccessCb?: () => void;
  }): Promise<TProfileData> => {
    return await requestWithZodSchema(() => updateProfile(body), ProfileDataResponseSchema)
      .then((data) => {
        notifySuccess(i18n.t('notifyMessages.profile.detailsChangeSuccess'));
        onSuccessCb?.();
        return data;
      })
      .catch((error) => {
        notifyError(i18n.t('notifyMessages.profile.dataUpdateError'));
        throw error;
      });
  },
);

export const requestWalletsDataThunkAction = createAsyncThunk(
  'profile/fetchWallets',
  async (): Promise<TWalletsData> =>
    await requestWithZodSchema(requestWallets, WalletsResponseSchema).catch((error) => {
      notifyError(i18n.t('notifyMessages.profile.walletsLoadError'));
      throw error;
    }),
);

export const requestWalletApproveOtpThunkAction = createAsyncThunk(
  'profile/fetchWalletApproveOtp',
  async (body: IWalletApproveRequestBody, { dispatch }): Promise<TWalletsApproveData> =>
    await requestWithZodSchema(() => requestWalletApprove(body), WalletsApproveResponseSchema)
      .then((res) => {
        notifySuccess(i18n.t('notifyMessages.profile.confirmCodeCreateSuccess'));
        return res;
      })
      .catch((error) => {
        dispatch(setWalletChangeStatusAction('not-confirmed'));
        notifyError(i18n.t('notifyMessages.profile.confirmWalletChangeError'));
        throw error;
      }),
);

//update and createWallet have the same endpoints, body, return and leads to the same result. So we can use update thunk action in all places
export const updateWalletsDataThunkAction = createAsyncThunk(
  'profile/updateWallets',
  async (body: IUpdateWalletRequestBody, { dispatch }): Promise<TWalletsData> => {
    return await requestWithZodSchema(() => updateWallet(body), WalletsResponseSchema)
      .then((response: TWalletsData) => {
        dispatch(setWalletChangeStatusAction('initial'));
        dispatch(setNewWalletAddressAction(null));
        dispatch(closePopupAction());
        notifySuccess(i18n.t('notifyMessages.profile.confirmWalletChangeSuccess'));
        return response;
      })
      .catch((error: AxiosError<IUpdateWalletError>) => {
        dispatch(setWalletChangeStatusAction('not-confirmed'));
        if (error.response?.data.data) {
          notifyError(error.response.data.data.replace('_', ' '));
          throw error;
        }
        if (error.response?.data.message) {
          notifyError(i18n.t('notifyMessages.profile.invalidOrExpiredConfirmationCode'));
          throw error;
        }
        notifyError(i18n.t('notifyMessages.profile.walletAddressUpdateError'));
        throw error;
      });
  },
);

export const requestOrdersHistoryThunkAction = createAsyncThunk(
  'profile/fetchOrdersHistory',
  async (): Promise<TOrdersHistoryData> => {
    return await requestWithZodSchema(requestOrdersHistory, OrdersHistoryResponseSchema);
  },
);

export const requestWalletChangeFaceIdAction = createAsyncThunk(
  'profile/fetchWalletChangeFaceId',
  async (body: IChangeWalletRequestBody): Promise<TChangeWalletData> => {
    return await requestWithZodSchema(
      () => requestWalletChangeFaceId(body),
      ChangeWalletResponseSchema,
    ).catch((error: AxiosError<IChangeWalletError>) => {
      const errorMessage = error.response?.data.data;
      errorMessage && notifyError(String(errorMessage));
      throw error;
    });
  },
);
